import {
  autoUpdate,
  computePosition,
  flip,
  inline,
  offset,
  shift,
  type VirtualElement,
} from "@floating-ui/dom"
import type { LinkDialogOrigin } from "@shared/unified-tiptap/extensions/link/link-inline-extension"
import { tracked } from "@supergrain/kernel/react"
import { isTextSelection, posToDOMRect, type Editor } from "@tiptap/core"
import { NodeSelection } from "@tiptap/pm/state"
import { CellSelection } from "@tiptap/pm/tables"
import type { EditorView } from "@tiptap/pm/view"
import { motion, useReducedMotion } from "motion/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { type SurfaceArbiter } from "../../surface-arbiter"
import { Toolbar } from "./Toolbar"

/**
 * Reveal timing. Mouse selections reveal INSTANTLY at pointer release — the release
 * is the "selection finished" signal. This settle delay exists only for selections
 * with no completion signal (keyboard shift+arrow, programmatic), so the toolbar
 * doesn't chase a selection that is still growing; it restarts on every selection
 * change. Hiding is always immediate.
 */
const SHOW_SETTLE_MS = 150

function hasEditorView(editor: Editor): boolean {
  try {
    return Boolean(editor.view.dom)
  } catch {
    return false
  }
}

/** The rects the toolbar positions against — the real selection on screen. */
function selectionRects(view: EditorView): DOMRect[] {
  const { selection } = view.state

  if (selection instanceof NodeSelection) {
    let node = view.nodeDOM(selection.from) as HTMLElement | null
    const wrapper = node?.dataset.nodeViewWrapper
      ? node
      : node?.querySelector("[data-node-view-wrapper]")
    if (wrapper) node = wrapper as HTMLElement
    if (node) return [node.getBoundingClientRect()]
  }

  if (selection instanceof CellSelection) {
    const { $anchorCell, $headCell } = selection
    const fromDOM = view.nodeDOM(($anchorCell ?? $headCell)!.pos) as HTMLElement | null
    const toDOM = view.nodeDOM(($headCell ?? $anchorCell)!.pos) as HTMLElement | null
    if (fromDOM && toDOM) {
      return fromDOM === toDOM
        ? [fromDOM.getBoundingClientRect()]
        : [fromDOM.getBoundingClientRect(), toDOM.getBoundingClientRect()]
    }
  }

  // Prefer the browser's own selection rects (they track the painted selection
  // exactly, including native mouse selections); fall back to ProseMirror's.
  const native = window.getSelection()
  if (native && native.rangeCount > 0 && !native.isCollapsed) {
    const range = native.getRangeAt(0)
    if (view.dom.contains(range.commonAncestorContainer)) {
      const rects = [...range.getClientRects()].filter((r) => r.width > 0 && r.height > 0)
      if (rects.length > 0) return rects
    }
  }
  return [posToDOMRect(view, selection.from, selection.to)]
}

function combineRects(rects: DOMRect[]): DOMRect {
  const top = Math.min(...rects.map((r) => r.top))
  const left = Math.min(...rects.map((r) => r.left))
  const bottom = Math.max(...rects.map((r) => r.bottom))
  const right = Math.max(...rects.map((r) => r.right))
  return new DOMRect(left, top, right - left, bottom - top)
}

/**
 * The formatting toolbar surface: OWNS NOTHING about visibility. It reports
 * `isSelectionEligible` from its boundary (transactions, focus/blur, the settle
 * timer) and paints whatever the arbiter decides; positioning is floating-ui
 * over the live selection rects. Replaces the forked tiptap bubble-menu plugin.
 * tracked(): the paint reads `arbiter.visible.selectionToolbar` as a signal.
 */
export const FormattingToolbar = tracked(
  ({
    editor,
    arbiter,
    openLinkDialog,
  }: {
    editor: Editor
    arbiter: SurfaceArbiter
    openLinkDialog: (origin: LinkDialogOrigin) => void
  }) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const surfaceRef = useRef<HTMLDivElement | null>(null)
    // Readiness is keyed to the editor INSTANCE: hosts can swap in a fresh editor
    // whose view is still deferred, and a plain boolean would survive the swap and
    // lie about the new editor.
    const [readyEditor, setReadyEditor] = useState<Editor | null>(() =>
      hasEditorView(editor) ? editor : null,
    )
    const viewReady = readyEditor === editor

    // Escape dismisses the toolbar without touching the selection. The dismissal is
    // keyed to the selection it was made on and clears on the first selection change,
    // so the next selection re-arms the toolbar.
    const dismissedSelectionRef = useRef<Editor["state"]["selection"] | null>(null)
    // The selection the toolbar is anchored to: the last one that passed
    // eligibleNow(). Position derives from this snapshot, never from the live
    // selection — so a transaction that disqualifies the selection can't drag the
    // still-visible toolbar to the new caret before the hide lands.
    const anchorSelectionRef = useRef<Editor["state"]["selection"] | null>(null)
    const dismissToolbar = useCallback(() => {
      dismissedSelectionRef.current = editor.state.selection
      arbiter.report({ isToolbarDismissed: true })
    }, [arbiter, editor])

    // In some hosts the editor mounts after this component (editor.view throws
    // before create) — defer all view access until then.
    useEffect(() => {
      if (hasEditorView(editor)) {
        setReadyEditor(editor)
        return
      }
      const markViewReady = () => setReadyEditor(editor)
      editor.on("create", markViewReady)
      return () => {
        editor.off("create", markViewReady)
      }
    }, [editor])

    // The selection boundary: derive this surface's input and report it. The arbiter
    // decides; dragging and overlays are its business, not this component's.
    useEffect(() => {
      // viewReady is keyed to this editor, but re-check the live editor anyway:
      // touching a deferred view throws, and no timing surprise may ever crash a host.
      if (!viewReady || !hasEditorView(editor)) return
      const view = editor.view

      const focusInToolbar = () => surfaceRef.current?.contains(document.activeElement) ?? false

      const eligibleNow = (): boolean => {
        const { doc, selection } = view.state
        // Range min/max instead of selection.from/to, for CellSelections.
        const from = Math.min(...selection.ranges.map((range) => range.$from.pos))
        const to = Math.max(...selection.ranges.map((range) => range.$to.pos))
        // `empty` alone is not enough: double-clicking an empty paragraph yields a
        // node-sized selection around no text.
        const isEmptyTextBlock = !doc.textBetween(from, to).length && isTextSelection(selection)
        // Focus inside the toolbar (or its dropdowns, portaled within it) counts as
        // editor focus — interacting with the toolbar must not hide it.
        const hasEditorFocus = view.hasFocus() || focusInToolbar()
        return hasEditorFocus && !selection.empty && !isEmptyTextBlock && editor.isEditable
      }

      let settleTimer: number | null = null
      let hideTimer: number | null = null
      // Deferred push()es (see pushSoon). Tracked so teardown can cancel any still
      // pending — a push() firing against a destroyed view would read a stale/torn-down
      // editor.
      const pushSoonTimers = new Set<number>()
      let revealPending = false
      let pointerIsDown = false

      const completeReveal = () => {
        revealPending = false
        if (eligibleNow()) arbiter.report({ isSelectionEligible: true })
      }

      const push = () => {
        // An Escape dismissal lasts as long as the selection it was made on.
        if (
          dismissedSelectionRef.current &&
          !view.state.selection.eq(dismissedSelectionRef.current)
        ) {
          dismissedSelectionRef.current = null
          arbiter.report({ isToolbarDismissed: false })
        }

        if (!eligibleNow()) {
          if (settleTimer !== null) window.clearTimeout(settleTimer)
          settleTimer = null
          revealPending = false
          // Confirm ineligibility a tick later: focus can be mid-flight (on <body>)
          // when a transaction fires during a focus move into the toolbar, and a
          // transient hide would blur whatever was just focused there.
          if (!arbiter.reported.isSelectionEligible || hideTimer !== null) return
          hideTimer = window.setTimeout(() => {
            hideTimer = null
            if (!eligibleNow()) arbiter.report({ isSelectionEligible: false })
          }, 0)
          return
        }
        anchorSelectionRef.current = view.state.selection
        if (hideTimer !== null) {
          window.clearTimeout(hideTimer)
          hideTimer = null
        }
        if (arbiter.reported.isSelectionEligible) return // already revealed: stay put

        // Focus pinning (invariant 1): while the toolbar contains focus it must be
        // visible NOW, not after a settle delay.
        if (focusInToolbar()) {
          revealPending = false
          arbiter.report({ isSelectionEligible: true })
          return
        }

        // Becoming eligible: reveal once the selection settles (timer restarts on
        // every change) and never mid-mouse-selection.
        revealPending = true
        if (settleTimer !== null) window.clearTimeout(settleTimer)
        settleTimer = window.setTimeout(() => {
          settleTimer = null
          if (!revealPending || pointerIsDown) return
          completeReveal()
        }, SHOW_SETTLE_MS)
      }
      // Focus can bounce through <body> for a tick when it moves between the editor
      // and react-aria controls (Chrome) — read it after it lands.
      const pushSoon = () => {
        const id = window.setTimeout(() => {
          pushSoonTimers.delete(id)
          push()
        })
        pushSoonTimers.add(id)
      }

      const onPointerDown = () => {
        pointerIsDown = true
      }
      const onPointerUp = () => {
        pointerIsDown = false
        // Releasing the mouse completes the selection — reveal NOW, no settle.
        if (revealPending) {
          if (settleTimer !== null) {
            window.clearTimeout(settleTimer)
            settleTimer = null
          }
          completeReveal()
        }
      }

      // Both directions, because react-aria routes focus through <body> between closing a
      // popover and restoring focus to its trigger. Listening only for `focusout` leaves the
      // hide reported mid-flight standing forever; the gap is unbounded, so no delay covers it.
      // Read `relatedTarget`, not `activeElement`, for the same reason.
      const surfaceEl = surfaceRef.current
      const onSurfaceFocusOut = (event: FocusEvent) => {
        const next = event.relatedTarget
        if (next instanceof Node && surfaceEl?.contains(next)) return // moved within
        pushSoon()
      }
      surfaceEl?.addEventListener("focusout", onSurfaceFocusOut)
      surfaceEl?.addEventListener("focusin", push)

      editor.on("transaction", push)
      editor.on("focus", pushSoon)
      editor.on("blur", pushSoon)
      view.dom.addEventListener("pointerdown", onPointerDown)
      window.addEventListener("pointerup", onPointerUp)
      return () => {
        surfaceEl?.removeEventListener("focusout", onSurfaceFocusOut)
        surfaceEl?.removeEventListener("focusin", push)
        editor.off("transaction", push)
        editor.off("focus", pushSoon)
        editor.off("blur", pushSoon)
        view.dom.removeEventListener("pointerdown", onPointerDown)
        window.removeEventListener("pointerup", onPointerUp)
        if (settleTimer !== null) window.clearTimeout(settleTimer)
        if (hideTimer !== null) window.clearTimeout(hideTimer)
        pushSoonTimers.forEach((id) => window.clearTimeout(id))
      }
    }, [editor, arbiter, viewReady])

    // Keyboard access from the editor: Alt+F10 (the ARIA APG / Google Docs
    // convention) moves focus into the visible toolbar — its react-aria Toolbar
    // takes over from there (arrow-key navigation) — and Escape dismisses it.
    useEffect(() => {
      if (!viewReady || !hasEditorView(editor)) return
      const dom = editor.view.dom
      const onKeyDown = (event: KeyboardEvent) => {
        if (!arbiter.visible.selectionToolbar) return
        if (event.key === "Escape") {
          dismissToolbar()
          return
        }
        if (event.key !== "F10" || !event.altKey) return
        const control = containerRef.current?.querySelector<HTMLElement>("button")
        if (!control) return
        event.preventDefault()
        control.focus()
      }
      dom.addEventListener("keydown", onKeyDown)
      return () => dom.removeEventListener("keydown", onKeyDown)
    }, [editor, arbiter, dismissToolbar, viewReady])

    // Paint: show exactly when the arbiter says so; position over the selection.
    // A tracked signal read — this component re-renders only when the verdict flips.
    const visible = arbiter.visible.selectionToolbar
    const reduceMotion = useReducedMotion()

    // The controls are built on first reveal, not on mount. Each one probes
    // `editor.can()` for its disabled state, and every probe allocates a transaction and
    // rebuilds the whole command table — ~15 of those per editor, and a planner mounts an
    // editor per lesson, so a week of lessons paid for a toolbar none of them could show
    // yet (measured: 27% of all mount-time JS). Latched during render, so the reveal that
    // flips `visible` is the same render that mounts them — a frame later and the
    // positioning effect below would measure an empty surface.
    const hasBeenRevealedRef = useRef(false)
    if (visible) hasBeenRevealedRef.current = true
    const showsControls = hasBeenRevealedRef.current

    useEffect(() => {
      if (!visible || !viewReady || !hasEditorView(editor)) return
      const surface = surfaceRef.current
      if (!surface) return
      const view = editor.view

      // Rects for the ANCHORED selection. While the live selection still equals the
      // anchor, the browser's own rects are best (they track the painted selection);
      // once the live selection has moved on, the toolbar holds its last anchor —
      // the arbiter's hide is what removes it, not a lunge to the new caret.
      const anchorRects = (): DOMRect[] => {
        const anchor = anchorSelectionRef.current
        if (!anchor || view.state.selection.eq(anchor)) return selectionRects(view)
        return [posToDOMRect(view, anchor.from, anchor.to)]
      }

      const reference: VirtualElement = {
        getBoundingClientRect: () => combineRects(anchorRects()),
        getClientRects: () => anchorRects(),
        contextElement: view.dom,
      }
      const updatePosition = () => {
        void computePosition(reference, surface, {
          placement: "bottom",
          middleware: [offset(8), flip(), shift(), inline()],
        }).then(({ x, y }) => {
          surface.style.left = `${x}px`
          surface.style.top = `${y}px`
        })
      }
      updatePosition()
      // autoUpdate covers scroll/resize/layout shifts; transactions cover the
      // selection itself moving while the toolbar stays up.
      const stopAutoUpdate = autoUpdate(view.dom, surface, updatePosition)
      editor.on("transaction", updatePosition)
      return () => {
        stopAutoUpdate()
        editor.off("transaction", updatePosition)
      }
    }, [visible, viewReady, editor])

    return (
      <div id="unified-tiptap-bubble-menu" ref={containerRef}>
        {/* Once revealed, kept mounted (hidden via visibility/opacity) so popovers inside
            it don't flash-reopen when remounted, and so suppressed ≠ removed for a11y.
            Fades both ways (WHEN it shows is still the settle/pointerup logic's job
            — this only softens the paint). The hide
            fades in place; visibility flips only after the fade so the toolbar
            stays in the a11y tree until it's really gone. pointer-events cuts a
            mid-fade toolbar off from clicks immediately. */}
        <motion.div
          ref={surfaceRef}
          style={{
            position: "absolute",
            width: "max-content",
            pointerEvents: visible ? undefined : "none",
          }}
          initial={false}
          animate={
            visible
              ? { opacity: 1, visibility: "visible" }
              : { opacity: 0, transitionEnd: { visibility: "hidden" } }
          }
          transition={reduceMotion ? { duration: 0 } : { duration: 0.12, ease: "easeOut" }}
        >
          {showsControls && (
            <Toolbar editor={editor} openLinkDialog={openLinkDialog} onDismiss={dismissToolbar} />
          )}
        </motion.div>
      </div>
    )
  },
)
